import { useEffect, useMemo, useRef } from 'react';
import useWebpageVisualSeoAnalysisProgress from './useWebpageVisualSeoAnalysisProgress';
import { useLazyFetchWebpageVisualSeoAnalysisQuery } from '../../service';
import { WebpageVisualSeoAnalysis } from '../../types';


type WebpageVisualSeoAnalysisHook = {
    data: WebpageVisualSeoAnalysis | undefined;
    isInProgress: boolean;
    isLoading: boolean;
    isFetchingLatestAnalysis: boolean;
    error: unknown;
};

/**
 * Orchestrates the webpage visual SEO analysis lifecycle for a given { wGuid, url }:
 * - If an analysis process is active, we let the progress hook poll and expose loading.
 * - If no active process, that means at least one analysis has completed. In this case we fetch
 *   the latest analysis. A 404/no-data is treated as a non-error.
 * Because `GET /active` auto-creates a process on first visit, the first load yields an active
 * process rather than an empty page.
 */
export default function useWebpageVisualSeoAnalysis(wGuid: string, url: string): WebpageVisualSeoAnalysisHook {
    const { hasActiveProcess, isLoading: isLoadingActiveProcess } = useWebpageVisualSeoAnalysisProgress(wGuid, url);
    const [fetchLatestAnalysis, fetchLatestAnalysisState] = useLazyFetchWebpageVisualSeoAnalysisQuery();

    // Prevent duplicate orchestration while conditions remain stable
    const attemptedRef = useRef(false);
    const inFlightFetchRef = useRef<ReturnType<typeof fetchLatestAnalysis> | null>(null);
    const isUnmountedRef = useRef(false);

    useEffect(() => {
        return () => {
            isUnmountedRef.current = true;
        };
    }, []);

    useEffect(() => {
        // Only orchestrate when not actively analyzing and not loading the active-process status
        if (hasActiveProcess || isLoadingActiveProcess) return;
        if (!wGuid || !url) return;

        // Avoid repeated triggers while state is stable
        if (attemptedRef.current) return;
        if (fetchLatestAnalysisState.isFetching) return;

        attemptedRef.current = true;
        const latestAnalysisPromise = fetchLatestAnalysis({ wGuid, url });
        inFlightFetchRef.current = latestAnalysisPromise;

        latestAnalysisPromise.unwrap()
            .catch(() => {
                return Promise.resolve();
            })
            .finally(() => {
                inFlightFetchRef.current = null;
            });

        // Cleanup: abort in-flight fetch if unmounting
        return () => {
            if (isUnmountedRef.current) {
                try {
                    inFlightFetchRef.current?.abort?.();
                } catch (_) { }
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wGuid, url, hasActiveProcess, isLoadingActiveProcess, fetchLatestAnalysis]);

    // Reset the attempted flag when conditions change (e.g., active process starts/stops or webpage switches)
    useEffect(() => {
        if (hasActiveProcess || isLoadingActiveProcess) {
            attemptedRef.current = false;
        }
    }, [hasActiveProcess, isLoadingActiveProcess]);

    useEffect(() => {
        attemptedRef.current = false;
    }, [wGuid, url]);

    const isFetchingLatestAnalysis = !!fetchLatestAnalysisState.isFetching;
    const isLoading = !!(isLoadingActiveProcess || isFetchingLatestAnalysis);

    const non404Error = useMemo(() => {
        const e: any = fetchLatestAnalysisState.error;
        if (!e) return undefined;
        const status = (e && typeof e === 'object' && 'status' in e) ? e.status : e?.originalStatus;
        return status === 404 ? undefined : e;
    }, [fetchLatestAnalysisState.error]);

    return {
        data: fetchLatestAnalysisState.data,
        isInProgress: hasActiveProcess,
        isLoading,
        isFetchingLatestAnalysis,
        error: non404Error,
    };
}
